import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/primitives/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/primitives/Dialog';
import { Input } from '@/components/primitives/Input';
import { PhoneInput } from '@/components/primitives/PhoneInput';
import { useTranslation } from '@/hooks/useTranslation';
import { onboardingQuery } from '@/modules/auth/queries';
import { signupApi } from '@/modules/auth/services/signupApi';
import { useAuthStore } from '@/modules/auth/stores/useAuthStore';
import { responseErrors } from '@/modules/settings/utils/responseErrors';
import { appEvents } from '@/services/appEvents';

import { AUTH_TITLE_CLASS } from './AuthLayout';

import type { PhoneValue } from '@/components/primitives/PhoneInput';
import type { OnboardingState } from '@/modules/auth/services/signupApi';
import type { OnboardingOutcome, OnboardingTrigger } from '@/services/appEvents';

const DEFERRED_KEY = 'nabat.onboarding.deferred';
/** The dialog's save button is 40px tall, between the m and l scales. */
export function OnboardingModal() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const deferredKey = `${DEFERRED_KEY}.${userId}`;
  const [open, setOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState<PhoneValue>({ country: 'AE', national: '' });
  const [phoneChanged, setPhoneChanged] = useState(false);

  const onboarding = useQuery(onboardingQuery(userId));

  // Emitted once per mount, however onboarding settles.
  const resolved = useRef(false);
  const resolve = useCallback((outcome: OnboardingOutcome) => {
    if (resolved.current) return;
    resolved.current = true;
    appEvents.emit('onboarding.resolved', { outcome });
  }, []);

  const openWith = useCallback((data: OnboardingState, trigger: OnboardingTrigger) => {
    setOrgName(data.org_name || data.suggested_org_name);
    setJobTitle(data.job_title);
    // A stored number comes back in E.164; the picker holds it split, so it has to be taken apart
    // again — otherwise the field looks empty to someone who has already given us their number.
    const stored = parsePhoneNumberFromString(data.phone || '');
    // With no number yet, default to the signup's country (the starter AOI's), else the UAE.
    const signupCountry = data.country_code;
    if (stored?.country) setPhone({ country: stored.country, national: stored.nationalNumber });
    else if (signupCountry && isSupportedCountry(signupCountry))
      setPhone({ country: signupCountry, national: '' });
    setPhoneChanged(false);
    setOpen(true);
    appEvents.emit('onboarding.modal_opened', { trigger });
  }, []);

  const autoOpened = useRef(false);
  useEffect(() => {
    if (!onboarding.data) return;
    if (!onboarding.data.is_free_tier) return resolve('not_needed');
    if (onboarding.data.completed) return resolve('completed');
    if (sessionStorage.getItem(deferredKey)) return resolve('deferred');
    // Once per mount: StrictMode replays this effect when the data is already cached.
    if (autoOpened.current) return;
    autoOpened.current = true;
    openWith(onboarding.data, 'auto');
  }, [onboarding.data, deferredKey, resolve, openWith]);

  // Unmounted while open (a route change): close it for listeners, so every open is matched.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(
    () => () => {
      if (openRef.current)
        appEvents.emit('onboarding.modal_closed', { reason: 'navigated', completed: false });
    },
    []
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      signupApi.completeOnboarding({
        ...(onboarding.data?.can_rename_organisation && { org_name: orgName.trim() }),
        job_title: jobTitle.trim(),
        ...(phoneChanged && {
          phone: phone.national.trim(),
          phone_country: phone.country,
        }),
      }),
    onSuccess: (saved) => {
      setOpen(false);
      appEvents.emit('onboarding.modal_closed', { reason: 'saved', completed: saved.completed });
      queryClient.setQueryData(onboardingQuery(userId).queryKey, (current) =>
        current ? { ...current, ...saved } : current
      );
      if (saved.completed) {
        resolve('completed');
        return;
      }
      // Partial save: treat as deferred for this session.
      sessionStorage.setItem(deferredKey, '1');
      resolve('deferred');
    },
  });

  const errors = responseErrors(saveMutation.error);
  const formError = errors.non_field_errors || errors.detail;
  const canRename = onboarding.data?.can_rename_organisation ?? false;

  const dismiss = () => {
    sessionStorage.setItem(deferredKey, '1');
    setOpen(false);
    appEvents.emit('onboarding.modal_closed', { reason: 'dismissed', completed: false });
    resolve('deferred');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A save in flight settles the dialog itself; don't let a dismissal race it.
        if (!next && !saveMutation.isPending) dismiss();
      }}
    >
      <DialogContent
        width="s"
        showClose={false}
        overlayClassName="bg-background/60 backdrop-blur-md"
        className="max-w-[372px] gap-xxl overflow-visible rounded-[12px] px-xxl py-xxxl"
      >
        <div className="flex flex-col gap-sm">
          <DialogTitle className={AUTH_TITLE_CLASS}>
            {onboarding.data?.display_name
              ? t('Welcome, {name}', { name: onboarding.data.display_name })
              : t('Welcome')}
          </DialogTitle>
          <DialogDescription className="text-size-n1 leading-[22px] text-foreground-muted">
            {t('Tell us a little about you. Everything here is optional.')}
          </DialogDescription>
        </div>

        <form
          className="flex flex-col gap-xxl"
          onSubmit={(event) => {
            event.preventDefault();
            if (!saveMutation.isPending) saveMutation.mutate();
          }}
        >
          {canRename && (
            <Input
              label={t('Organisation')}
              autoComplete="organization"
              value={orgName}
              onChange={(event) => setOrgName(event.target.value)}
              error={errors.org_name}
            />
          )}
          <Input
            label={t('Role or job title')}
            placeholder={t('e.g. Restoration lead')}
            value={jobTitle}
            onChange={(event) => setJobTitle(event.target.value)}
            error={errors.job_title}
          />
          <PhoneInput
            label={t('Phone number')}
            value={phone}
            onChange={(value) => {
              setPhone(value);
              setPhoneChanged(true);
            }}
            error={errors.phone}
          />
          {formError && (
            <p role="alert" className="text-sm text-status-danger">
              {formError}
            </p>
          )}
          <div className="pt-sm">
            <Button
              type="submit"
              appearance="solid"
              kind="inverse"
              scale="m"
              width="full"
              loading={saveMutation.isPending}
            >
              {t('Continue')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
